import { useApolloClient } from '@apollo/client'
import { format, formatDistanceToNow } from 'date-fns'
import { Snowflake } from 'lucide-react'
import { Suspense, useEffect } from 'react'
import { COMPANY_FREEZE_QUERY } from '@/components/layout/company-freeze-notice/query'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import { subscribeToCompanyFrozen } from '@/lib/graphql/company-frozen'
import {
  useIdCompany,
  usePageVisibility,
  useRBAC,
  useSuspenseQuery,
} from '@/lib/hooks'
import type {
  CompanyFreezeQuery,
  CompanyFreezeQueryVariables,
} from '@/components/layout/company-freeze-notice/query.types.gen'
import type { CompanyFreezeReason } from '@/graphql.types'

const POLL_INTERVAL_MS = 60_000

type FreezeCopy = {
  title: string
  description: string
  details: string[]
}

const MAINTENANCE_COPY: FreezeCopy = {
  title: 'Maintenance in progress',
  description:
    'Your company is under maintenance. Your services keep running, but deployments and other changes are paused for now.',
  details: [
    'Git pushes are not deployed. Deploy again when changes are back.',
    'You can still view everything in the dashboard.',
  ],
}

const FREEZE_COPY: Partial<Record<string, FreezeCopy>> = {
  migration: {
    title: 'Your migration is in progress',
    description:
      'We are moving your company to Oracle Cloud Infrastructure. Your services keep running, but deployments and other changes are paused until the migration finishes.',
    details: [
      'Git pushes are not deployed. Deploy again after the migration.',
      'Pull request preview apps are not created.',
      'You can still view everything in the dashboard.',
    ],
  },
  maintenance: MAINTENANCE_COPY,
  reverseMigration: MAINTENANCE_COPY,
} satisfies Record<CompanyFreezeReason, FreezeCopy>

const CompanyFreezeNoticeContent = ({ idCompany }: { idCompany: string }) => {
  const client = useApolloClient()
  const isPageVisible = usePageVisibility()
  const { rbac } = useRBAC()

  const { data } = useSuspenseQuery<
    CompanyFreezeQuery,
    CompanyFreezeQueryVariables
  >(COMPANY_FREEZE_QUERY, { variables: { idCompany }, errorPolicy: 'all' })

  useEffect(() => {
    const refetch = () => {
      client
        .refetchQueries({ include: [COMPANY_FREEZE_QUERY] })
        .catch(() => undefined)
    }
    const unsubscribe = subscribeToCompanyFrozen(refetch)

    if (!isPageVisible) {
      return unsubscribe
    }

    const idInterval = setInterval(refetch, POLL_INTERVAL_MS)

    return () => {
      unsubscribe()
      clearInterval(idInterval)
    }
  }, [client, isPageVisible])

  const frozenAt = data?.company?.frozenAt

  if (!frozenAt) {
    return null
  }

  const { title, description, details } =
    FREEZE_COPY[data?.company?.frozenReason ?? 'maintenance'] ??
    MAINTENANCE_COPY
  const isSuperadmin = rbac.kinsta().superadmin.can()

  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-2 border-sky-500/40 bg-sky-500/10 px-3 text-xs font-medium text-sky-800 hover:bg-sky-500/15 hover:text-sky-900 dark:text-sky-300 dark:hover:text-sky-200"
          >
            <Snowflake aria-hidden="true" className="size-3.5" />
            Changes paused
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="flex w-80 flex-col gap-3">
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium">{title}</span>
            <p className="text-sm text-muted-foreground">{description}</p>
          </div>
          <ul className="flex list-disc flex-col gap-1 pl-4 text-sm text-muted-foreground">
            {details.map((detail) => (
              <li key={detail}>{detail}</li>
            ))}
          </ul>
          <p className="text-xs text-muted-foreground">
            Paused{' '}
            <time
              dateTime={frozenAt}
              title={format(new Date(frozenAt), 'PPpp')}
            >
              {formatDistanceToNow(new Date(frozenAt), { addSuffix: true })}
            </time>
            {isSuperadmin ? '. Superadmins are not blocked.' : null}
          </p>
        </PopoverContent>
      </Popover>
      <Separator orientation="vertical" className="h-4!" />
    </>
  )
}

export const CompanyFreezeNotice = () => {
  const { idCompany } = useIdCompany()

  if (!idCompany) {
    return null
  }

  return (
    <Suspense fallback={null}>
      <CompanyFreezeNoticeContent idCompany={idCompany} />
    </Suspense>
  )
}
